'use client'

import { useEffect, useMemo, useState } from 'react'
import {
  Country,
  getCountryCallingCode,
  parsePhoneNumber
} from 'react-phone-number-input'

import { PhoneInput as BasePhoneInput } from '@/components/ui/phone-input'

import { isFieldInvalid } from '@/utils'

import { FieldWrapper } from './field-wrapper'
import { useFieldContext } from './form'

// Detect country based on user's timezone
const getCountryFromTimezone = (): string => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    // Map common timezones to countries
    const timezoneToCountry: Record<string, string> = {
      'Africa/Algiers': 'DZ',
      'Africa/Cairo': 'EG',
      // North Africa
      'Africa/Casablanca': 'MA',
      'Africa/Khartoum': 'SD',
      'Africa/Tripoli': 'LY',
      'Africa/Tunis': 'TN',
      'America/Chicago': 'US',
      'America/Denver': 'US',
      'America/Los_Angeles': 'US',
      // Americas
      'America/New_York': 'US',
      'America/Toronto': 'CA',
      'Asia/Amman': 'JO',
      'Asia/Baghdad': 'IQ',
      'Asia/Bahrain': 'BH',

      'Asia/Beirut': 'LB',
      'Asia/Damascus': 'SY',
      'Asia/Dubai': 'AE',
      'Asia/Gaza': 'PS',
      'Asia/Hong_Kong': 'HK',

      'Asia/Jerusalem': 'IL',
      'Asia/Karachi': 'PK',
      'Asia/Kolkata': 'IN',
      'Asia/Kuwait': 'KW',
      'Asia/Muscat': 'OM',

      'Asia/Qatar': 'QA',
      // Middle East
      'Asia/Riyadh': 'SA',
      'Asia/Seoul': 'KR',
      // Asia
      'Asia/Shanghai': 'CN',
      'Asia/Singapore': 'SG',

      'Asia/Tehran': 'IR',
      'Asia/Tokyo': 'JP',
      'Europe/Berlin': 'DE',
      // Europe
      'Europe/London': 'GB',
      'Europe/Madrid': 'ES',
      'Europe/Paris': 'FR',
      'Europe/Rome': 'IT'
    }

    // Return mapped country or default to Saudi Arabia
    return timezoneToCountry[timezone] || 'SA'
  } catch {
    // Fallback to Saudi Arabia if detection fails
    return 'SA'
  }
}

/**
 * Normalizes a phone number to E.164 format (no spaces)
 * Handles various formats like "224.668.5267", "(224) 668-5267", "+20 4242793530", etc.
 * @param value - Phone number in any format
 * @param defaultCountry - Default country code to use if number doesn't have one
 * @returns E.164 formatted phone number (without spaces) or undefined
 */
const normalizePhoneNumber = (
  value: string | undefined,
  defaultCountry?: Country
): string | undefined => {
  if (!value || value.trim() === '') {
    return undefined
  }

  // Remove all spaces first to handle cases like "+20 4242793530"
  const valueWithoutSpaces = value.replace(/\s/g, '')

  // If already in E.164 format (starts with +), try to parse it
  if (valueWithoutSpaces.startsWith('+')) {
    try {
      const parsed = parsePhoneNumber(valueWithoutSpaces)
      // parsed.number should be in E.164 format, but strip spaces just in case
      return parsed?.number?.replace(/\s/g, '') || valueWithoutSpaces
    } catch {
      return valueWithoutSpaces
    }
  }

  // Remove all non-digit characters except +
  const digitsOnly = valueWithoutSpaces.replace(/[^\d+]/g, '')

  // If empty after cleaning, return undefined
  if (!digitsOnly || digitsOnly.length === 0) {
    return undefined
  }

  // If it already starts with +, try to parse it
  if (digitsOnly.startsWith('+')) {
    try {
      const parsed = parsePhoneNumber(digitsOnly)
      return parsed?.number?.replace(/\s/g, '') || digitsOnly
    } catch {
      return digitsOnly
    }
  }

  // Try to parse with default country
  if (defaultCountry) {
    try {
      const parsed = parsePhoneNumber(digitsOnly, defaultCountry)
      return parsed?.number?.replace(/\s/g, '') || undefined
    } catch {
      // If parsing fails, try to construct E.164 format manually
      // This is a fallback for cases where parsePhoneNumber fails
      try {
        const countryCode = getCountryCallingCode(defaultCountry)
        if (countryCode && digitsOnly.length >= 7) {
          return `+${countryCode}${digitsOnly}`
        }
      } catch {
        // If getCountryCallingCode fails, return undefined
      }
    }
  }

  // If no default country and number doesn't start with +, return undefined
  // The component will handle this gracefully
  return undefined
}

/**
 * Validates if a phone number is valid
 * @param value - Phone number to validate
 * @returns Error message if invalid, undefined if valid
 */
export const validatePhoneNumber = (
  value: string | undefined
): string | undefined => {
  // If empty, no validation error (let required field validation handle it)
  if (!value || value.trim() === '') {
    return undefined
  }

  // Basic validation - check if it contains only digits, spaces, parentheses, dashes, and plus
  const phoneRegex = /^[\+]?[\d\s\(\)\-\.]+$/
  if (!phoneRegex.test(value)) {
    return 'Invalid phone number format'
  }

  // Check minimum length (at least 7 digits)
  const digitsOnly = value.replace(/\D/g, '')
  if (digitsOnly.length < 7) {
    return 'Phone number is too short'
  }

  // Check maximum length (reasonable limit)
  if (digitsOnly.length > 15) {
    return 'Phone number is too long'
  }

  return undefined
}

export function PhoneInput({
  disabled,
  label,
  placeholder,
  required
}: {
  disabled?: boolean
  label?: string
  placeholder?: string
  required?: boolean
}) {
  const field = useFieldContext()
  const isInvalid = isFieldInvalid(field)
  const [localError, setLocalError] = useState<null | string>(null)

  // Detect country from user's timezone (only once)
  const defaultCountry = useMemo(() => getCountryFromTimezone(), [])

  // Normalize the initial value when it's first loaded from backend
  // This handles cases where the backend sends formats like "(424) 279-3530"
  useEffect(() => {
    const rawValue = field.state.value as string | undefined
    if (rawValue && rawValue.trim() !== '') {
      const normalized = normalizePhoneNumber(
        rawValue,
        defaultCountry as Country
      )
      // Only update if normalization changed the value (e.g., removed spaces, added country code)
      if (normalized && normalized !== rawValue) {
        field.handleChange(normalized)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.state.value]) // Run when value changes to normalize initial load

  // Normalize the phone value to E.164 format before passing to BasePhoneInput
  const normalizedValue = useMemo(() => {
    const rawValue = field.state.value as string | undefined
    return normalizePhoneNumber(rawValue, defaultCountry as Country)
  }, [field.state.value, defaultCountry])

  const handleChange = (value?: null | string) => {
    // Normalize the value to E.164 format (remove spaces, ensure proper format)
    const normalizedValue = value
      ? (normalizePhoneNumber(value, defaultCountry as Country) ??
        value.replace(/\s/g, ''))
      : ''

    // Update the field value with normalized (space-free) version
    field.handleChange(normalizedValue)

    // Validate immediately on change
    if (normalizedValue.trim() !== '') {
      const error = validatePhoneNumber(normalizedValue)
      setLocalError(error || null)
    } else {
      setLocalError(null)
    }
  }

  // Validate on blur and ensure final value is normalized
  const handleBlur = () => {
    field.handleBlur()

    // Get current value and normalize it (remove spaces)
    const currentValue = (field.state.value as string | undefined) ?? ''
    const normalizedValue = currentValue
      ? (normalizePhoneNumber(currentValue, defaultCountry as Country) ??
        currentValue.replace(/\s/g, ''))
      : ''

    // Update field with normalized value if it changed
    if (normalizedValue !== currentValue && normalizedValue !== '') {
      field.handleChange(normalizedValue)
    }

    // Trigger validation after blur
    if (normalizedValue.trim() !== '') {
      const error = validatePhoneNumber(normalizedValue)
      setLocalError(error || null)
    }
  }

  return (
    <FieldWrapper field={field} label={label} required={required}>
      <BasePhoneInput
        aria-invalid={isInvalid || !!localError}
        className="w-full"
        data-invalid={isInvalid || !!localError || undefined}
        defaultCountry={defaultCountry as Country}
        disabled={disabled}
        id={field.name}
        international
        onBlur={handleBlur}
        onChange={handleChange}
        placeholder={placeholder}
        value={normalizedValue}
        variant="md"
      />
    </FieldWrapper>
  )
}

// Export as PhoneInputField for form compatibility
export const PhoneInputField = PhoneInput
